import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../store/auth';

/**
 * In dev the Vite proxy forwards /api → localhost:3000.
 * In production point VITE_API_BASE_URL to https://api.permio.fr.
 */
const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export const http = axios.create({ baseURL: BASE });

/**
 * Returns a valid Bearer token, refreshing it silently when it is about to
 * expire using the credentials stored in the auth store.
 */
async function getValidToken(): Promise<string | null> {
  const store = useAuthStore.getState();

  if (store.isTokenValid()) return store.token;

  const { clientId, clientSecret } = store;
  if (!clientId || !clientSecret) return null;

  try {
    const { data } = await axios.post(`${BASE}/api/v1/crm/auth/token`, {
      grant_type: 'client_credentials',
      client_id: clientId,
      client_secret: clientSecret,
    });
    store.setAuth(data.access_token, data.expires_in, clientId, clientSecret);
    return data.access_token as string;
  } catch {
    store.clearAuth();
    return null;
  }
}

http.interceptors.request.use(async (config) => {
  const token = await getValidToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

http.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().clearAuth();
    }
    return Promise.reject(err);
  },
);
